import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActiveInteraction, useClients, useUsers, userMap } from "@/lib/hooks";
import { ClientContacts } from "@/components/ClientContacts";
import { PageHeader } from "@/components/PageHeader";
import { StartInteractionSheet } from "@/components/StartInteractionSheet";
import { SERVICES, eurFmt, hasFinancialAccess } from "@/lib/constants";
import { fileToDataUrl } from "@/lib/files";
import { fmtDuration, safeUrl } from "@/lib/utils";
import { LogoBgField } from "@/components/LogoBgField";
import { UserAvatar } from "@/components/UserAvatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Building2, GripVertical, Search, Square, Timer } from "lucide-react";

/** Ordena pela ordem guardada; os que não estão lá (novos) vão para o fim. */
function sortByOrder(list, ids) {
  const position = new Map(ids.map((id, index) => [id, index]));
  const rank = (id) => (position.has(id) ? position.get(id) : Number.MAX_SAFE_INTEGER);
  return [...list].sort((a, b) => rank(a.id) - rank(b.id));
}

export default function Clients() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canSeeFinancials = hasFinancialAccess(user);
  const { data: clients = [], isLoading } = useClients();
  const { data: users = [] } = useUsers();
  const um = userMap(users);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name: "", nif: "", contacts: [], logoUrl: "", logoBg: "", services: [], associateIds: [], retainerValue: "" });
  const [saving, setSaving] = useState(false);
  const [interactionClient, setInteractionClient] = useState(null);
  const { data: activeData } = useActiveInteraction();
  const activeInteraction = activeData?.interaction || null;

  const stopInteraction = async () => {
    try {
      const { data: closed } = await api.post("/interactions/stop");
      qc.invalidateQueries({ queryKey: ["interaction-active"] });
      qc.invalidateQueries({ queryKey: ["interactions"] });
      toast.success(`Interação em ${closed.clientName} terminada · ${fmtDuration(closed.minutes)}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const handleInteractionClick = (event, client) => {
    event.stopPropagation();
    if (activeInteraction?.clientId === client.id) stopInteraction();
    else setInteractionClient(client);
  };

  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, logoUrl: "" }));
    const url = await fileToDataUrl(file);
    setForm((f) => ({ ...f, logoUrl: url }));
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Indique o nome da empresa");
    setSaving(true);
    try {
      const payload = { ...form };
      if (canSeeFinancials) payload.retainerValue = parseFloat(form.retainerValue) || 0;
      else delete payload.retainerValue;
      const { data } = await api.post("/clients", payload);
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente criado");
      setOpen(false);
      setForm({ name: "", nif: "", contacts: [], logoUrl: "", logoBg: "", services: [], associateIds: [], retainerValue: "" });
      navigate(`/clientes/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // Cada pessoa arruma os cartões à sua maneira; a ordem fica guardada na conta.
  const { data: savedOrder = [] } = useQuery({
    queryKey: ["client-order"],
    queryFn: async () => (await api.get("/client-order")).data.order,
  });
  const [dragOrder, setDragOrder] = useState(null); // ordem ao vivo enquanto se arrasta
  const [dragId, setDragId] = useState(null);
  const droppedRef = useRef(false);
  const ordered = useMemo(() => sortByOrder(clients, dragOrder || savedOrder), [clients, dragOrder, savedOrder]);
  // Com pesquisa ativa só se vê parte da lista: arrastar aí baralhava o resto.
  const canDrag = !q.trim();

  const startDrag = (event, id) => {
    event.dataTransfer.effectAllowed = "move";
    try { event.dataTransfer.setData("text/plain", id); } catch { /* ignore */ }
    droppedRef.current = false;
    setDragOrder(ordered.map((c) => c.id));
    setDragId(id);
  };

  const dragOverCard = (event, targetId) => {
    if (!dragId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (targetId === dragId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const after = event.clientX > rect.left + rect.width / 2;
    setDragOrder((current) => {
      const next = current.filter((id) => id !== dragId);
      const index = next.indexOf(targetId) + (after ? 1 : 0);
      next.splice(index, 0, dragId);
      return next.join() === current.join() ? current : next;
    });
  };

  // Largar fora da grelha (ou Esc) desfaz; largar dentro grava a ordem nova.
  const endDrag = async () => {
    const next = dragOrder;
    const before = sortByOrder(clients, savedOrder).map((c) => c.id);
    setDragId(null);
    setDragOrder(null);
    if (!droppedRef.current || !next || next.join() === before.join()) return;
    const previous = savedOrder;
    qc.setQueryData(["client-order"], next);
    try {
      await api.put("/client-order", { order: next });
    } catch (e) {
      qc.setQueryData(["client-order"], previous);
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const filtered = ordered.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader
        title="Base de Clientes"
        subtitle={`${clients.length} cliente(s) na carteira.`}
        actions={
          <Button onClick={() => setOpen(true)} className="bg-primary text-black hover:bg-primary/90 rounded-lg" data-testid="new-client-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Novo Cliente
          </Button>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="relative w-full max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar cliente..." className="pl-9" data-testid="client-search" />
        </div>
        {clients.length > 1 && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <GripVertical className="h-3.5 w-3.5" />
            {canDrag ? "Arrasta os cartões para os organizar." : "Limpa a pesquisa para reorganizar."}
          </p>
        )}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">A carregar...</p>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground font-roboto">Ainda não há clientes. Crie o primeiro.</p>
        </Card>
      ) : (
        <div
          className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4"
          onDragOver={(e) => { if (dragId) e.preventDefault(); }}
          onDrop={(e) => { if (!dragId) return; e.preventDefault(); droppedRef.current = true; }}
        >
          {filtered.map((c) => {
            const logo = safeUrl(c.logoUrl) || safeUrl(c.logos?.[0]?.dataUrl);
            const isTracking = activeInteraction?.clientId === c.id;
            return (
            <Card
              key={c.id}
              onClick={() => navigate(`/clientes/${c.id}`)}
              draggable={canDrag}
              onDragStart={(e) => startDrag(e, c.id)}
              onDragOver={(e) => dragOverCard(e, c.id)}
              onDragEnd={endDrag}
              className={`overflow-hidden p-0 border-border hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer ${dragId === c.id ? "opacity-40 ring-2 ring-primary/40" : ""}`}
              data-testid={`client-card-${c.id}`}
            >
              <div
                className="relative h-24 flex items-center justify-center border-b border-border bg-muted"
                style={c.logoBg ? { backgroundColor: c.logoBg } : undefined}
              >
                {logo
                  ? <img src={logo} alt={c.name} draggable={false} className="max-h-14 max-w-[68%] object-contain" />
                  : <Building2 className="h-8 w-8 text-muted-foreground/50" />}
                <button
                  type="button"
                  onClick={(e) => handleInteractionClick(e, c)}
                  className={`absolute top-2 left-2 flex h-6 w-6 items-center justify-center rounded-full border transition-colors ${
                    isTracking
                      ? "border-primary bg-primary text-black"
                      : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-primary"
                  }`}
                  title={isTracking ? "Parar interação" : "Gerar interação"}
                  aria-label={isTracking ? "Parar interação" : "Gerar interação"}
                  data-testid={`interaction-btn-${c.id}`}
                >
                  {isTracking ? <Square className="h-3 w-3 fill-current" /> : <Timer className="h-3.5 w-3.5" />}
                </button>
                <span className="absolute top-2 right-2 flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium">
                  <span className={`h-1.5 w-1.5 rounded-full ${c.status === "ATIVO" ? "bg-primary" : "bg-muted-foreground"}`} />
                  {c.status === "ATIVO" ? "Ativo" : "Inativo"}
                </span>
              </div>

              <div className="p-5">
              <h3 className="font-poppins font-semibold truncate mb-2">{c.name}</h3>
              <div className="flex flex-wrap gap-1.5 mb-3 min-h-[24px]">
                {(c.services || []).slice(0, 3).map((s) => (
                  <Badge key={s} variant="secondary" className="rounded-full text-[10px]">{s}</Badge>
                ))}
                {(c.services || []).length > 3 && <Badge variant="secondary" className="rounded-full text-[10px]">+{c.services.length - 3}</Badge>}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex items-center gap-2">
                  {(() => {
                    const ids = c.associateIds && c.associateIds.length
                      ? c.associateIds
                      : [c.accountManagerId, c.trafficManagerId].filter(Boolean);
                    const people = ids.map((id) => um[id]).filter(Boolean);
                    if (people.length === 0) return <span className="text-xs text-muted-foreground">Sem associados</span>;
                    return (
                      <>
                        <div className="flex -space-x-1.5">
                          {people.slice(0, 4).map((p) => <UserAvatar key={p.id} user={p} size="h-6 w-6 ring-1 ring-card" />)}
                        </div>
                        {people.length > 4 && <span className="text-xs text-muted-foreground">+{people.length - 4}</span>}
                      </>
                    );
                  })()}
                </div>
                {canSeeFinancials && c.retainerValue > 0 && <span className="text-xs font-poppins font-semibold text-primary">{eurFmt.format(c.retainerValue)}/mês</span>}
              </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="client-modal">
          <DialogHeader><DialogTitle className="font-poppins">Novo Cliente</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nome da empresa</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="client-name" />
              </div>
              <div className={canSeeFinancials ? "" : "col-span-2"}>
                <Label>NIF</Label>
                <Input value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} className="mt-1" />
              </div>
              {canSeeFinancials && <div>
                <Label>Avença mensal (€)</Label>
                <Input type="number" value={form.retainerValue} onChange={(e) => setForm({ ...form, retainerValue: e.target.value })} className="mt-1" />
              </div>}
            </div>
            <ClientContacts contacts={form.contacts} onChange={(contacts) => setForm({ ...form, contacts })} />
            <div>
              <Label>Associados</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Quem trabalha com este cliente.</p>
              <div className="flex flex-wrap gap-2">
                {users.map((u) => {
                  const active = form.associateIds.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, associateIds: active ? f.associateIds.filter((x) => x !== u.id) : [...f.associateIds, u.id] }))}
                      className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border text-xs transition-colors ${active ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/50"}`}
                    >
                      <UserAvatar user={u} size="h-5 w-5" />
                      {u.name.split(" ")[0]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-3">
              <Label>Logótipo</Label>
              <div className="flex items-center gap-3 mt-1">
                <div
                  className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden"
                  style={form.logoBg ? { backgroundColor: form.logoBg } : undefined}
                >
                  {safeUrl(form.logoUrl) ? <img src={safeUrl(form.logoUrl)} className="h-full w-full object-contain" alt="" /> : <Building2 className="h-5 w-5 text-muted-foreground" />}
                </div>
                <Input type="file" accept="image/*" onChange={onLogo} className="text-sm" />
              </div>
              <LogoBgField value={form.logoBg} onChange={(v) => setForm((f) => ({ ...f, logoBg: v }))} />
            </div>
            <div>
              <Label>Serviços</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {SERVICES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleService(s)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${form.services.includes(s) ? "bg-primary text-black border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={create} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="client-save">
              {saving ? "A criar..." : "Criar Cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StartInteractionSheet
        client={interactionClient}
        open={Boolean(interactionClient)}
        onOpenChange={(next) => { if (!next) setInteractionClient(null); }}
      />
    </div>
  );
}
